import { useState, useRef } from "react";
import { format } from "date-fns";
import axios from "axios";
import { ArrowLeft, CreditCard, FileText, Loader2, MapPin, User, Wrench, Building2, Mail, Phone, Upload, X, Paperclip } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Step4Summary({ formData, onPrev, user }) {
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileB64, setUploadedFileB64] = useState(null);
  const fileInputRef = useRef(null);

  const activeWorks = formData.works.filter((w) => w.quantity > 0);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file only");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }
    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(",")[1];
      setUploadedFileB64(b64);
    };
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setUploadedFile(null);
    setUploadedFileB64(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const notificationPayload = {
        certificate_ref: formData.certificate_ref,
        installation_address: formData.installation_address,
        description: formData.description,
        installation_date: formData.installation_date ? format(formData.installation_date, "yyyy-MM-dd") : "",
        country: formData.country,
        region: formData.region,
        is_new_build: formData.is_new_build,
        premise: formData.premise,
        street: formData.street,
        locality: formData.locality,
        town: formData.town,
        county: formData.county,
        postcode: formData.postcode,
        local_authority: formData.local_authority,
        local_authority_email: formData.local_authority_email,
        homeowner_email: formData.homeowner_email,
        homeowner_name: formData.homeowner_name,
        homeowner_phone: formData.homeowner_phone,
        works: formData.works.filter((w) => w.quantity > 0),
        extra_attachment_b64: uploadedFileB64 || "",
        extra_attachment_name: uploadedFile?.name || "",
      };

      const { data: notification } = await axios.post(`${API}/notifications`, notificationPayload, { withCredentials: true });

      const origin = window.location.origin;
      const { data: checkout } = await axios.post(
        `${API}/payments/create-checkout`,
        { notification_id: notification.id, origin_url: origin },
        { withCredentials: true }
      );

      if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        toast.error("Failed to create payment session");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to process. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="step-4-summary">
      <div className="form-container mb-6">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-6 h-6 text-[#D4A537]" />
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Building Notification
          </h2>
        </div>
        <p className="text-sm text-[#71717A] mb-8">
          Review all details before proceeding to payment. A certificate will be generated and emailed after payment.
        </p>

        {/* Certificate Details */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-[#D4A537]" />
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A537]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Certificate Details
            </h3>
          </div>
          <div className="border-2 border-[#E4E4E7] divide-y-2 divide-[#E4E4E7]">
            <SummaryRow label="Certificate Ref" value={formData.certificate_ref} testid="summary-cert-ref" />
            <SummaryRow label="Installation Address" value={formData.installation_address} testid="summary-install-addr" />
            <SummaryRow label="Description" value={formData.description} testid="summary-description" />
            <SummaryRow
              label="Installation Date"
              value={formData.installation_date ? format(formData.installation_date, "dd/MM/yyyy") : ""}
              testid="summary-install-date"
            />
          </div>
        </div>

        {/* Address */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-[#D4A537]" />
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A537]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Installation Address
            </h3>
          </div>
          <div className="border-2 border-[#E4E4E7] divide-y-2 divide-[#E4E4E7]">
            <SummaryRow label="Address" value={[formData.premise, formData.street, formData.locality, formData.town, formData.county].filter(Boolean).join(", ")} testid="summary-full-address" />
            <SummaryRow label="Postcode" value={formData.postcode} testid="summary-postcode" />
            <SummaryRow label="Country" value={formData.country} testid="summary-country" />
            <SummaryRow label="Region" value={formData.region} testid="summary-region" />
            <SummaryRow label="New Build" value={formData.is_new_build ? "Yes" : "No"} testid="summary-new-build" />
            <SummaryRow label="Local Authority" value={formData.local_authority} testid="summary-authority" />
            <SummaryRow label="Authority Email" value={formData.local_authority_email} testid="summary-authority-email" mono />
          </div>
        </div>

        {/* Customer */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-[#D4A537]" />
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A537]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Customer Details
            </h3>
          </div>
          <div className="border-2 border-[#E4E4E7] divide-y-2 divide-[#E4E4E7]">
            <SummaryRow label="Name" value={formData.homeowner_name} testid="summary-owner-name" icon={<User className="w-3.5 h-3.5" />} />
            <SummaryRow label="Email" value={formData.homeowner_email} testid="summary-owner-email" icon={<Mail className="w-3.5 h-3.5" />} mono />
            <SummaryRow label="Phone" value={formData.homeowner_phone} testid="summary-owner-phone" icon={<Phone className="w-3.5 h-3.5" />} mono />
          </div>
        </div>

        {/* Technician */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-[#D4A537]" />
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A537]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Technician
            </h3>
          </div>
          <div className="border-2 border-[#E4E4E7] divide-y-2 divide-[#E4E4E7]">
            <SummaryRow label="Name" value={user?.name} testid="summary-tech-name" />
            <SummaryRow label="Company" value={user?.company} testid="summary-tech-company" />
            <SummaryRow label="Email" value={user?.email} testid="summary-tech-email" mono />
          </div>
        </div>

        {/* Works */}
        {activeWorks.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4 text-[#D4A537]" />
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A537]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Works Completed
              </h3>
            </div>
            <div className="border-2 border-[#0A0A0A]">
              <div className="grid grid-cols-12 bg-[#D4A537] px-4 py-2.5">
                <div className="col-span-9 text-white text-xs uppercase tracking-[0.15em] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Description</div>
                <div className="col-span-3 text-white text-xs uppercase tracking-[0.15em] font-bold text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Quantity</div>
              </div>
              {activeWorks.map((w, i) => (
                <div key={i} data-testid={`summary-work-${i}`} className={`grid grid-cols-12 px-4 py-2.5 border-t border-[#E4E4E7] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'}`}>
                  <div className="col-span-9 text-sm">{w.name}</div>
                  <div className="col-span-3 text-center font-bold mono" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{w.quantity}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upload Certificate PDF */}
      <div className="brutalist-card p-6 mb-6" data-testid="upload-section">
        <div className="flex items-center gap-2 mb-3">
          <Paperclip className="w-5 h-5 text-[#D4A537]" />
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A537]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Attach Certificate PDF (Optional)
          </h3>
        </div>
        <p className="text-sm text-[#71717A] mb-4">
          Upload an additional certificate PDF to send alongside the building notification to the customer.
        </p>

        {!uploadedFile ? (
          <label
            data-testid="upload-dropzone"
            className="flex flex-col items-center justify-center border-2 border-dashed border-[#B8B9BD] p-8 cursor-pointer hover:border-[#D4A537] hover:bg-[#FEFCE8] transition-all"
          >
            <Upload className="w-8 h-8 text-[#B8B9BD] mb-2" />
            <span className="text-sm font-medium text-[#0A0A0A]">Click to upload a PDF</span>
            <span className="text-xs text-[#71717A] mt-1">PDF files only, max 10MB</span>
            <input
              ref={fileInputRef}
              data-testid="upload-file-input"
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        ) : (
          <div className="flex items-center gap-3 border-2 border-[#D4A537] bg-[#FEFCE8] p-4" data-testid="uploaded-file-info">
            <FileText className="w-8 h-8 text-[#D4A537] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A0A0A] truncate">{uploadedFile.name}</p>
              <p className="text-xs text-[#71717A]">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
            </div>
            <button
              data-testid="remove-upload-button"
              onClick={removeFile}
              className="w-8 h-8 flex items-center justify-center border-2 border-[#0A0A0A] bg-white hover:bg-red-50 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Payment */}
      <div className="brutalist-card p-6 mb-6" data-testid="payment-section">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#71717A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Notification Fee
            </p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="payment-amount">
              &pound;4.99
            </p>
            <p className="text-xs text-[#71717A] mt-1">Certificate will be emailed to all parties after payment</p>
          </div>
          <CreditCard className="w-10 h-10 text-[#D4A537]" />
        </div>
      </div>

      <div className="flex justify-between">
        <button data-testid="step-4-prev-button" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <button
          data-testid="pay-button"
          onClick={handlePayment}
          disabled={loading}
          className="brutalist-btn flex items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" /> Pay &pound;4.99 &amp; Submit
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, testid, mono, icon }) {
  return (
    <div className="grid grid-cols-12 px-4 py-2.5" data-testid={testid}>
      <div className="col-span-4 text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {icon}
        {label}
      </div>
      <div className={`col-span-8 text-sm text-[#0A0A0A] ${mono ? 'mono' : ''}`} style={mono ? { fontFamily: 'IBM Plex Mono, monospace' } : {}}>
        {value || "—"}
      </div>
    </div>
  );
}
